import { z } from 'zod';
import { TeamSchema } from './primitives.js';

export const TimeControlIdSchema = z.enum(['fast', 'standard', 'long', 'custom']);
export type TimeControlId = z.infer<typeof TimeControlIdSchema>;

/** Limits for custom time controls. */
export const CUSTOM_LIMITS = Object.freeze({
  minInitialMs: 30_000,
  maxInitialMs: 3 * 60 * 60_000,
  maxIncrementMs: 60_000,
});

export const TimeControlSchema = z
  .object({
    id: TimeControlIdSchema,
    initialMs: z.number().int().min(CUSTOM_LIMITS.minInitialMs).max(CUSTOM_LIMITS.maxInitialMs),
    incrementMs: z.number().int().min(0).max(CUSTOM_LIMITS.maxIncrementMs),
  })
  .strict();
export type TimeControl = z.infer<typeof TimeControlSchema>;

/** Preset time controls. Placeholders: the server may override them through its config. */
export const TIME_CONTROLS: Readonly<Record<Exclude<TimeControlId, 'custom'>, TimeControl>> =
  Object.freeze({
    fast: { id: 'fast', initialMs: 3 * 60_000, incrementMs: 2_000 },
    standard: { id: 'standard', initialMs: 10 * 60_000, incrementMs: 5_000 },
    long: { id: 'long', initialMs: 30 * 60_000, incrementMs: 0 },
  });

/** A preset by id, or a custom time control with explicit values. */
export const TimeControlRequestSchema = z.union([
  z.object({ id: z.enum(['fast', 'standard', 'long']) }).strict(),
  TimeControlSchema.extend({ id: z.literal('custom') }),
]);
export type TimeControlRequest = z.infer<typeof TimeControlRequestSchema>;

export function resolveTimeControl(request: TimeControlRequest): TimeControl {
  return request.id === 'custom' ? request : TIME_CONTROLS[request.id];
}

/**
 * Remaining time per team when the message was sent, and whose clock is running. Clients count
 * down locally from these values; the server stays the authority and declares timeouts.
 */
export const ClocksSchema = z
  .object({
    I: z.number().int().min(0),
    B: z.number().int().min(0),
    running: TeamSchema.nullable(),
  })
  .strict();
export type Clocks = z.infer<typeof ClocksSchema>;
